import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useFetchFilters } from '../../../hooks/useFetchFilters';
import FilterBox from './FilterBox/FilterBox';
import {
    ButtonsWrapper,
    ButtonWrapper,
    ChosenFilterButton,
    ChosenFiltersWrapper,
    ClearButton,
    Close,
    FiltersWrapper,
    FunctionButtonsWrapper,
    Reset,
} from './ProductsSearchFiltersStyle';
import useBrowserWidth from '../../../hooks/useBrowserWidth';
import MobileFilterBox from './MobileFilterBox/MobileFilterBox';
import FilterButton from './FilterButton/FilterButton';
import { ProductSearchResultsContext } from '../../../context/ProductSearchResultsContext';
import { TABLET_SCREEN_SIZE } from '../../../styles/MediaQuery';

const ProductsSearchFilters = () => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState([]);
    const [activeCategory, setActiveCategory] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const [isClicked, setIsClicked] = useState(false);
    const {
        typeFilter,
        setTypeFilter,
        modeFilter,
        setModeFilter,
        pressureRangeFilter,
        setPressureRangeFilter,
        searchFilter,
        setSearchFilter,
        heatRecoveryFilter,
        setHeatRecoveryFilter,
        soundAbsorptionFilter,
        setSoundAbsorptionFilter,
        deliveryControlFilter,
        setDeliveryControlFilter,
        coolingFilter,
        setCoolingFilter,
        otherFilter,
        setOtherFilter,
        currentCategoryName,
        setCurrentCategoryName,
        setCurrentCategoryId,
        compressionModeUnits,
        setCompressionModeUnits,
        fedMinFilter,
        setFedMinFilter,
        fedMaxFilter,
        setFedMaxFilter,
        motorPowerMinFilter,
        setMotorPowerMinFilter,
        motorPowerMaxFilter,
        setMotorPowerMaxFilter,
        currentCategoryId,
        setIsProductsSearchFiltersOpened,
        setIsAllFiltersClear,
        isFiltersSubmit,
        motorPowerMaxValue,
        fedMaxValue,
    } = useContext(ProductSearchResultsContext);
    const [motorPowerButton, setMotorPowerButton] = useState('');
    const [fedButton, setFedButton] = useState('');
    const [motorPowerMax, setMotorPowerMax] = useState(motorPowerMaxFilter);
    const [motorPowerMin, setMotorPowerMin] = useState(motorPowerMinFilter);
    const [fedMin, setFedMin] = useState(fedMinFilter);
    const [fedMax, setFedMax] = useState(fedMaxFilter);
    const [isMotorPowerChanged, setIsMotorPowerChanged] = useState(false);
    const [isFedChanged, setIsFedChanged] = useState(false);
    const nameOfFilters = ['type', 'compression_mode', 'pressure_range', 'equipment'];
    const currentBrowserWidth = useBrowserWidth();
    const filterValue = 0;
    const setFunction = 1;

    const updateMotorPower = (): void => {
        if (motorPowerMax !== motorPowerMaxFilter) {
            setMotorPowerMax(motorPowerMaxFilter);
        }

        if (motorPowerMin !== motorPowerMinFilter) {
            setMotorPowerMin(motorPowerMinFilter);
        }

        if (motorPowerMaxFilter !== motorPowerMaxValue || motorPowerMinFilter !== 0) {
            setActiveCategory('');
            setIsMotorPowerChanged(true);
        } else {
            setIsMotorPowerChanged(false);
        }
    };

    const updateFed = (): void => {
        if (fedMax !== fedMaxFilter) {
            setFedMax(fedMaxFilter);
        }

        if (fedMin !== fedMinFilter) {
            setFedMin(fedMinFilter);
        }

        if (fedMaxFilter !== fedMaxValue || fedMinFilter !== 0) {
            setActiveCategory('');
            setIsFedChanged(true);
        } else {
            setIsFedChanged(false);
        }
    };

    useEffect(() => {
        updateMotorPower();
    }, [motorPowerMaxFilter, motorPowerMinFilter]);

    useEffect(() => {
        updateFed();
    }, [fedMinFilter, fedMaxFilter]);

    useEffect(() => {
        if (
            motorPowerMin !== undefined &&
            motorPowerMax !== undefined &&
            (motorPowerMin !== 0 || motorPowerMax !== motorPowerMaxValue)
        ) {
            if (compressionModeUnits === 'metric' && isMotorPowerChanged) {
                setMotorPowerButton(`${t('motorPower')} ${motorPowerMin}-${motorPowerMax} kW`);
            } else if (compressionModeUnits === 'imperial' && isMotorPowerChanged) {
                setMotorPowerButton(`${t('motorPower')} ${motorPowerMin}-${motorPowerMax} HP`);
            } else {
                setMotorPowerButton('');
            }
        } else {
            setMotorPowerButton('');
        }
    }, [compressionModeUnits, motorPowerMax, motorPowerMin]);

    useEffect(() => {
        if (fedMin !== undefined && fedMax !== undefined && (fedMin !== 0 || fedMax !== fedMaxValue)) {
            if (compressionModeUnits === 'metric' && isFedChanged) {
                setFedButton(`${t('fad')} ${fedMin}-${fedMax} m3/min`);
            } else if (compressionModeUnits === 'imperial' && isFedChanged) {
                setFedButton(`${t('fad')} ${fedMin}-${fedMax} cfm`);
            } else {
                setFedButton('');
            }
        } else {
            setFedButton('');
        }
    }, [compressionModeUnits, fedMin, fedMax]);

    const clearMotorPowerFilters = (): void => {
        if (!fedButton) {
            setCompressionModeUnits('');
        }
        setMotorPowerMinFilter(0);
        setMotorPowerMaxFilter(motorPowerMaxValue);
        setMotorPowerMin(0);
        setMotorPowerMax(motorPowerMaxValue);
        setMotorPowerButton('');
        setIsMotorPowerChanged(false);
    };

    const clearFedFilters = (): void => {
        if (!motorPowerButton) {
            setCompressionModeUnits('');
        }
        setFedMinFilter(0);
        setFedMaxFilter(fedMaxValue);
        setFedMin(0);
        setFedMax(fedMaxValue);
        setFedButton('');
        setIsFedChanged(false);
    };

    const filtersCurrentState = [
        [currentCategoryName, setCurrentCategoryName],
        [typeFilter, setTypeFilter],
        [modeFilter, setModeFilter],
        [pressureRangeFilter, setPressureRangeFilter],
        [searchFilter, setSearchFilter],
        [heatRecoveryFilter, setHeatRecoveryFilter],
        [soundAbsorptionFilter, setSoundAbsorptionFilter],
        [deliveryControlFilter, setDeliveryControlFilter],
        [coolingFilter, setCoolingFilter],
        [otherFilter, setOtherFilter],
        [motorPowerButton, clearMotorPowerFilters],
        [fedButton, clearFedFilters],
    ];

    const getFilters = async (): Promise<void> => {
        const data = await useFetchFilters();
        setFilters(data);
    };

    useEffect(() => {
        getFilters();
        setIsProductsSearchFiltersOpened(true);
    }, []);

    useEffect(() => {
        setActiveCategory('');
        if (
            !currentCategoryName &&
            !typeFilter &&
            !modeFilter &&
            !pressureRangeFilter &&
            !searchFilter &&
            !heatRecoveryFilter &&
            !soundAbsorptionFilter?.length &&
            !deliveryControlFilter?.length &&
            !coolingFilter?.length &&
            !otherFilter?.length &&
            ((!isMotorPowerChanged && !isFedChanged) || isFiltersSubmit)
        ) {
            setIsClicked(true);
        } else {
            setIsClicked(false);
        }
    }, [
        currentCategoryId,
        currentCategoryName,
        typeFilter,
        modeFilter,
        pressureRangeFilter,
        searchFilter,
        heatRecoveryFilter,
        soundAbsorptionFilter,
        deliveryControlFilter,
        coolingFilter,
        otherFilter,
        searchFilter,
        isMotorPowerChanged,
        isFedChanged,
        isFiltersSubmit,
    ]);

    const displayFiltersButtons = (): ReactNode =>
        nameOfFilters.map((item: any, index: number) => (
            <ButtonWrapper
                key={index}
                onClick={() => (activeCategory === item ? setActiveCategory('') : setActiveCategory(item))}
            >
                <FilterButton
                    activeCategory={activeCategory}
                    nameOfFilters={item}
                    setActiveCategory={setActiveCategory}
                    setIsVisible={setIsVisible}
                />
            </ButtonWrapper>
        ));

    const displayFiltersContent = (): ReactNode =>
        filters?.map((item: any, index: number) => (
            <FilterBox
                key={index}
                filterName={nameOfFilters[index]}
                filters={item}
                currentActiveCategory={activeCategory}
            />
        ));

    const displayChosenFilters = (): ReactNode =>
        filtersCurrentState.map((item: any, index) => {
            if (
                item[filterValue] !== undefined &&
                item[filterValue] !== 0 &&
                item[filterValue] !== '0' &&
                item[filterValue].length > 0 &&
                Array.isArray(item[filterValue])
            ) {
                return item[filterValue].map((itemOfArray: any, index) => (
                    <ChosenFilterButton key={`${itemOfArray}${index}`}>
                        {t(`${itemOfArray}`)}{' '}
                        <Close
                            onClick={() => item[setFunction](item[filterValue].filter((item) => item !== itemOfArray))}
                        />
                    </ChosenFilterButton>
                ));
            } else if (
                item[filterValue] !== undefined &&
                item[filterValue] !== 0 &&
                item[filterValue].length > 0 &&
                ((index === 5 && item[filterValue] !== 0) || item[filterValue] !== '0')
            ) {
                return (
                    <ChosenFilterButton key={`${item[filterValue]}${index}`}>
                        {t(`${item[filterValue]}`)}{' '}
                        <Close
                            onClick={() => item[setFunction](pressureRangeFilter ? false : heatRecoveryFilter ? 0 : '')}
                        />
                    </ChosenFilterButton>
                );
            }
        });

    const clearAllFilters = (): void => {
        setTypeFilter('');
        setModeFilter('');
        setPressureRangeFilter(0);
        setSearchFilter('');
        setHeatRecoveryFilter(0);
        setSoundAbsorptionFilter([]);
        setDeliveryControlFilter([]);
        setCoolingFilter([]);
        setOtherFilter([]);
        setCurrentCategoryName('');
        setCurrentCategoryId('');
        setFedMinFilter(0);
        setFedMaxFilter(fedMaxValue);
        setMotorPowerMinFilter(0);
        setMotorPowerMaxFilter(motorPowerMaxValue);
        setCompressionModeUnits('');
        setIsMotorPowerChanged(false);
        setIsFedChanged(false);
        setIsAllFiltersClear(true);
        setIsClicked(true);
    };

    const checkIsMobileView = (): ReactNode => {
        if (currentBrowserWidth <= TABLET_SCREEN_SIZE) {
            return <MobileFilterBox filtersName={nameOfFilters} filters={filters} />;
        } else {
            return (
                <>
                    <ButtonsWrapper>
                        {displayFiltersButtons()}
                        {displayFiltersContent()}
                    </ButtonsWrapper>
                    <FunctionButtonsWrapper>
                        <ChosenFiltersWrapper>{displayChosenFilters()}</ChosenFiltersWrapper>
                        {!isClicked && (
                            <ClearButton onClick={clearAllFilters}>
                                {t('clearAllFilters')} <Reset />
                            </ClearButton>
                        )}
                    </FunctionButtonsWrapper>
                </>
            );
        }
    };

    return <FiltersWrapper>{checkIsMobileView()}</FiltersWrapper>;
};

export default ProductsSearchFilters;
